// #region: Class definition *********************************************************************/
/**
 * @class PdfViewer
 * @description Custom web component that renders a PDF viewer.
 * Uses pdf.min.mjs (Mozilla - https://mozilla.github.io/pdf.js/).
 * Uses panzoom.js for zoom and pan functionality.
 * 
 * @extends WebComponent
 *
 * @example
 * <pdf-viewer src="Pdf-to-load" max-visible="5">
 *    <div class="loader k-loading-mask">
 *       <div class="k-loading-image"></div>
 *    </div>
 * </pdf-viewer>
 *
 * @property {string} max-visible - How many page numbers are maximum visible in the pager.
 * @property {string} src - The path to the pdf to view.
 *
 * @remarks
 *     The attributes are optional.
 */
class PdfViewer extends WebComponent {

    // #region: Static Data **********************************************************************/
    /**
    * Multiplier applied to the page's fit-page scale when computing the BASE canvas's render scale. `1` means the base bitmap matches the displayed
    * fit-page size (after DPR) — anything beyond fit-page is handled by the detail overlay, never by enlarging the base. Crank it up (1.5–2) on
    * desktops if you want a sharper "stretched" appearance during quick zooms before the overlay catches up; keep it at 1 on memory-constrained
    * mobile, where re-rasterising the whole blueprint at a higher scale is exactly what we're trying to avoid.
    * @type {number}
    * @static
    * @memberof PdfViewer
    */
    static BASE_OVERSAMPLE = 1;

    /**
     * Delay in milliseconds for debouncing resize events.
     * @type {number}
     * @static
     * @memberof PdfViewer
     */
    static DEBOUNCE_DELAY = 70;

    /**
     * Maximum canvas pixel limit for iOS devices to prevent memory issues.
     * @type {number}
     * @static
     * @memberof PdfViewer
     */
    static IOS_LIMIT = 16777216;

    /**
     * Maximum canvas pixels allowed, calculated based on device capabilities.
     * @type {number}
     * @static
     * @memberof PdfViewer
     */
    static MAX_CANVAS_PIXELS;

    /**
     * Maximum margin threshold for PDF scaling calculations.
     * @type {number}
     * @static
     * @memberof PdfViewer
     */
    static MAX_MARGIN = 0.85;

    /**
     * Maximum canvas pixel limit for non-iOS devices.
     * @type {number}
     * @static
     * @memberof PdfViewer
     */
    static OTHERS_LIMIT = 268435456;

    /**
     * Minimum scale threshold for determining significant scale changes.
     * @type {number}
     * @static
     * @memberof PdfViewer
     */
    static SCALE_TRESHOLD = 0.001;

    // #endregion ********************************************************************************/

    // #region: Private Fields *******************************************************************/
    /**
     * The content pager component for page navigation.
     * @type {ContentPager}
     * 
     * @memberof PdfViewer
     * @private
     */
    #contentPager;

    /**
     * The container div for the canvas elements.
     * @type {HTMLDivElement}
     */
    canvasContainer;

    /**
     * The current page number being displayed.
     * @type {number}
     */
    currentPage = 1;

    /**
     * The current zoom scale of the PDF.
     * @type {number}
     */
    currentScale = 1;

    /**
     * Off-DOM canvas used as a render buffer for the detail overlay. PDF.js paints into this first; once the render finishes we atomically blit it to
     * `#detailCanvas`, so the user never sees a flash of blank/white during the render window.
     * @type {?HTMLCanvasElement}
     *
     * @memberof PdfViewer
     * @private
     */
    #detailBuffer = null;

    /**
     * Overlay canvas that paints a sharp copy of the currently-visible region when `currentScale` exceeds the base canvas's `maxRenderScale`. Mirrors
     * the approach used by Mozilla's PDF.js viewer (`PDFPageDetailView`): the base bitmap stays small for memory, and only the viewport is
     * re-rasterised at the user's requested zoom.
     * @type {?HTMLCanvasElement}
     *
     * @memberof PdfViewer
     * @private
     */
    #detailCanvas = null;

    /**
     * Debounce timer for detail-overlay rendering. Coalesces rapid pan/zoom events into one render once the gesture settles.
     * @type {?number}
     *
     * @memberof PdfViewer
     * @private
     */
    #detailDebounceTimer = null;

    /**
     * Active PDF.js render task for the detail overlay. A new detail render cancels the previous task so the overlay never paints a stale viewport.
     * @type {?object}
     *
     * @memberof PdfViewer
     * @private
     */
    #detailRenderTask = null;

    /**
     * The hidden canvas element used for double buffering.
     * @type {HTMLCanvasElement}
     * 
     * @memberof PdfViewer
     * @private
     */
    #hiddenCanvas;

    /**
     * Flag indicating whether to render in high resolution.
     * @type {boolean}
     * 
     * @memberof PdfViewer
     * @private
     */
    #highres = false;

    /**
     * Page number that produced the bitmap currently sitting on the BASE canvas. Together with `#lastBaseRenderScale` this lets `renderPage` skip the
     * full-page rasterisation when nothing about the base would change — the detail overlay handles any zoom-in beyond the captured base scale.
     * @type {?number}
     *
     * @memberof PdfViewer
     * @private
     */
    #lastBaseRenderPage = null;

    /**
     * Render scale that produced the base bitmap currently sitting on the visible canvas. Compared against the freshly-computed `renderScale` to
     * decide whether the base canvas needs a re-rasterisation. Mirrors Mozilla's `PDFPageView` strategy of holding the base bitmap steady at a
     * baseline scale while the detail view absorbs subsequent zoom changes.
     * @type {?number}
     *
     * @memberof PdfViewer
     * @private
     */
    #lastBaseRenderScale = null;

    /**
     * Last `maxScale` value pushed into Panzoom via `setOptions`. Used to skip redundant option updates from per-event clamping.
     * @type {?number}
     *
     * @memberof PdfViewer
     * @private
     */
    #lastEffectiveMaxScale = null;

    /**
     * The DOM element displaying the loader.
     * @type {HTMLDivElement}
     * 
     * @memberof PdfViewer
     * @private
     */
    #loader;

    /**
     * Cached locale resources for this component.
     * @type {Object}
     *
     * @private
     */
    #locales;

    // Zoom configuration.
    /**
     * Maximum allowed zoom scale.
     * @type {number}
     * 
     * @memberof PdfViewer
     * @private
     */
    #maxScale = 10;

    /**
     * Minimum allowed zoom scale.
     * @type {number}
     * 
     * @memberof PdfViewer
     * @private
     */
    #minScale = 0.125;

    /**
     * The main navigation container div.
     * @type {HTMLDivElement}
     */
    navigation;

    /**
     * The Panzoom instance for handling zoom and pan.
     * @type {?Panzoom}
     * 
     * @memberof PdfViewer
     * @private
     */
    #panzoom;

    /**
     * Pending scale value coalesced from `panzoomzoom` events. Drained on the next animation frame.
     * @type {?number}
     *
     * @memberof PdfViewer
     * @private
     */
    #pendingPanzoomScale = null;

    /**
     * Stores the page number that is pending to be rendered.
     * @type {?number}
     * 
     * @memberof PdfViewer
     * @private
     */
    #pageNumberPending = null;

    /**
     * Flag indicating if a page is currently being rendered.
     * @type {boolean}
     * 
     * @memberof PdfViewer
     * @private
     */
    #pageRendering = false;

    /**
     * Cache for page viewport dimensions.
     * `maxRenderScale` caps the BASE canvas at the largest scale whose pixel area (post-DPR) still fits within `MAX_CANVAS_PIXELS`. The user-facing
     * `currentScale` may exceed it; the detail overlay covers the resulting CSS-stretching with a viewport-only high-res render.
     * @type {Map<number, {baseHeight: number, baseWidth: number, maxRenderScale: number}>}
     *
     * @memberof PdfViewer
     * @private
     */
    #pageViewportCache = new Map();

    /**
     * The loaded PDF document object from PDF.js.
     * @type {?object}
     *
     * @memberof PdfViewer
     * @private
     */
    #pdfDoc = null;

    /**
     * Promise that resolves when PDF.js library is loaded and ready.
     * @type {Promise}
     * 
     * @memberof PdfViewer
     * @private
     */
    #pdfjsLibReady;

    /**
    * Last-decided render mode for the active source. One of `'tile'` (server-side tile pyramid is driving the canvas) or `'viewport'` (PDF.js render
    * with the viewport-detail overlay). Surfaced via the `render-mode` custom event and the `data-render-mode` attribute so embedders can label
    * A/B test runs in their UI without instrumenting the rendering pipeline directly.
    * @type {?('tile'|'viewport')}
    *
    * @memberof PdfViewer
    * @private
    */
    #renderMode = null;

    /**
     * Timeout ID for debounced rendering.
     * @type {?number}
     * 
     * @memberof PdfViewer
     * @private
     */
    #renderTimeout = null;

    // Layout.
    /**
     * ResizeObserver instance to handle component resizing.
     * @type {?ResizeObserver}
     * 
     * @memberof PdfViewer
     * @private
     */
    #resizeObserver;

    /**
     * The toolbar div containing controls.
     * @type {HTMLDivElement}
     */
    toolbar;

    /**
     * Total number of pages in the PDF document.
     * @type {number}
     */
    totalPages = 0;

    /**
     * The visible canvas element where the PDF page is rendered.
     * @type {HTMLCanvasElement}
     * 
     * @memberof PdfViewer
     * @private
     */
    #visibleCanvas;

    /**
     * The wrapper div around the canvas elements, used by Panzoom.
     * @type {HTMLDivElement}
     */
    wrapper;

    /**
     * The input element for zoom control.
     * @type {?HTMLInputElement}
     * 
     * @memberof PdfViewer
     * @private
     */
    #zoomControl;

    /**
     * RAF id used to coalesce `panzoomzoom` events into one frame.
     * @type {?number}
     *
     * @memberof PdfViewer
     * @private
     */
    #zoomFrame = null;

    /**
    * Definitions for zoom levels used in the dropdown and for display logic.
    * @type {Array<{label?: string, labelKey?: string, value: string, isSelectOption?: boolean, dynamic?: boolean}>}
    * 
    * @memberof PdfViewer
    * @private
    */
    #zoomLevelDefinitions;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of PdfViewer.
     * Initializes PDF.js worker, creates toolbar, canvas elements, navigation, and notification elements.
     */
    constructor() {
        super();

        // Register locale namespaces used by this component and cache resources.
        this.#locales = this.registerLocaleNamespace();

        // Maximum total pixels for canvas elements.
        // (iOS limit) is 4096 * 4096 = 16777216. 
        // For others the (lowest) limit is 16384 * 16384 = 268435456.
        // See: https://jhildenbiddle.github.io/canvas-size/#/?id=test-results
        // Take some margin on the maximum.
        PdfViewer.MAX_CANVAS_PIXELS = (this.isIos ? PdfViewer.IOS_LIMIT : PdfViewer.OTHERS_LIMIT) * PdfViewer.MAX_MARGIN;

        // Initialize PDF.js library loading promise.
        this.#pdfjsLibReady = this.#ensurePdfjsLibLoaded();

        // Define zoom levels.
        this.#zoomLevelDefinitions = [
            { dynamic: true, isSelectOption: true, labelKey: 'pageFit', value: 'page-fit' },
            { dynamic: true, isSelectOption: true, labelKey: 'pageWidth', value: 'page-width' },
            { isSelectOption: true, label: '50%', value: '0.5' },
            { isSelectOption: true, label: '75%', value: '0.75' },
            { isSelectOption: true, label: '100%', value: '1' },
            { isSelectOption: true, label: '125%', value: '1.25' },
            { isSelectOption: true, label: '150%', value: '1.5' },
            { isSelectOption: true, label: '200%', value: '2' },
            { isSelectOption: true, label: '300%', value: '3' },
            { isSelectOption: true, label: '400%', value: '4' }
        ];

        // Create toolbar.
        this.toolbar = document.createElement('tool-bar');
        this.appendChild(this.toolbar);

        // Create canvas container and elements.
        this.#createCanvasElements();

        // Create navigation.
        this.navigation = document.createElement('div');
        this.navigation.className = 'pdf-navigation navigation-container';
        this.append(this.navigation);

        // Create notification element.
        const notification = document.createElement('div');
        notification.className = 'zoom-limit-notification';
        notification.setAttribute('aria-live', 'polite');
        notification.textContent = resources?.pdfViewer?.maxZoomReached || 'Maximum zoom reached';

        this.appendChild(notification);

        this.#loader = this.querySelector('.loader');
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof PdfViewer
     */
    static tagName = 'pdf-viewer';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance
     * 
     * @memberof PdfViewer
     */
    toString() {
        return '[PdfViewer]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class
     * 
     * @static
     */
    static toString() {
        return '[PdfViewer]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class
     * 
     * @static
     */
    static version() {
        return '2.3.0';
    }
    // #endregion ********************************************************************************/

    // #region: Static Properties ****************************************************************/
    /**
     * Defines the observed attributes for the custom element.
     * @returns {string[]} Array of attribute names to observe
     * 
     * @static
     */
    static get observedAttributes() {
        return [...(super.observedAttributes || []), 'page', 'src'];
    }
    // #endregion ********************************************************************************/

    // #region: Properties ***********************************************************************/
    /**
     * Gets the visible canvas element for external access.
     * @returns {HTMLCanvasElement|null} The visible canvas element or null if not initialized.
     *
     * @memberof PdfViewer
     */
    get canvas() {
        return this.#visibleCanvas;
    }

    /**
     * Gets the Panzoom instance for external access.
     * @returns {Panzoom|null} The Panzoom instance or null if not initialized.
     *
     * @memberof PdfViewer
     */
    get panzoom() {
        return this.#panzoom;
    }

    /**
     * Gets the active render mode for the currently loaded source.
     * Mirrors the most recent `render-mode` custom event detail.
     * Returns `null` until a source has been routed (either to the tile pyramid or to PDF.js).
     * @returns {?('tile'|'viewport')} The current render mode.
     *
     * @memberof PdfViewer
     */
    get renderMode() {
        return this.#renderMode;
    }

    /**
     * Gets the maximum visible page numbers in the page navigation.
     * @returns {string} The number of pages (as a string)
     *
     * @memberof PdfViewer
     */
    get maxVisible() {
        return this.getAttribute('max-visible') || '5';
    }

    /**
     * Sets the maximum visible page numbers in the page navigation.
     * @param {string|number} value The number of pages
     * 
     * @memberof PdfViewer
     */
    set maxVisible(value) {
        this.setAttribute('max-visible', Number(value) > 0 ? value.toString() : '1');
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Called when observed attributes change.
     * @param {string} name The name of the attribute that changed
     * @param {string|null} oldValue The previous value of the attribute
     * @param {string|null} newValue The new value of the attribute
     * 
     * @memberof PdfViewer
     */
    async attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);

        if (oldValue === newValue) {
            return;
        }

        switch (name) {

            case 'page':
                {
                    const pageNum = parseInt(newValue, 10);

                    if (this.hasRenderableSource() && pageNum > 0 && pageNum <= this.totalPages) {
                        await this.#queueRenderPage(pageNum);
                    }
                }

                break;

            case 'src':
                // Load PDF if this is an actual change (not initial setup) or if we're transitioning from no source to a valid source.
                // Initial load is handled by the initialize() method after PDF.js is ready.
                if (oldValue !== null || (oldValue === null && newValue)) {
                    await this.#loadPDF(newValue);
                }

                break;

            default:
                break;

        }
    }

    /**
     * Called when the custom element is attached to the DOM.
     * Sets up zoom control, navigation, calculates button width, and adds resize observer.
     * 
     * @memberof PdfViewer
     */
    async connectedCallback() {
        super.connectedCallback();

        // Setup key components.
        this.#setupZoomControl();
        
        // Wait for content-pager to be defined before setting up navigation.
        await customElements.whenDefined('content-pager');
        this.#setupNavigation();
        
        // Add resize observer for the component.
        this.#resizeObserver = new ResizeObserver(this.handleResize.bind(this));
        this.#resizeObserver.observe(this);

        // Initial load if src is already set.
        const initialSrc = this.getAttribute('src');

        if (initialSrc && !this.#pdfDoc) {
            this.#loadPDF(initialSrc);
        }
        else {
            this.toggleLoader(false);
        }
    }

    /**
     * Cleanup. Called when the component is removed from the DOM.
     * Destroys PDF document, disconnects observers, destroys panzoom instance, and removes event listeners.
     * 
     * @memberof PdfViewer
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        // Cancel any in-flight detail render before the rest.
        this.#abortDetailRender();

        // Let a subclass tear down its own source (e.g. tile layers, polling, progress overlay).
        this.teardownSource();

        if (this.#zoomFrame !== null) {
            cancelAnimationFrame(this.#zoomFrame);
            this.#zoomFrame = null;
        }

        // Clean up PDF document.
        if (this.#pdfDoc) {
            this.#pdfDoc.destroy();
            this.#pdfDoc = null;
        }

        // Disconnect observers.
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
        }

        // Destroy panzoom instance.
        if (this.#panzoom) {
            this.#panzoom.destroy();
            this.#panzoom = null;
        }

        // Release canvas GPU memory (critical for iOS Safari memory limits).
        if (this.#visibleCanvas) {
            this.#visibleCanvas.width = 0;
            this.#visibleCanvas.height = 0;
        }

        if (this.#hiddenCanvas) {
            this.#hiddenCanvas.width = 0;
            this.#hiddenCanvas.height = 0;
        }

        if (this.#detailCanvas) {
            this.#detailCanvas.width = 0;
            this.#detailCanvas.height = 0;
        }

        if (this.#detailBuffer) {
            this.#detailBuffer.width = 0;
            this.#detailBuffer.height = 0;
            this.#detailBuffer = null;
        }

        // Remove event listeners.
        this.removeEventListener('keydown', this.handleKeyboardZoom);
        if (this.handleDocumentClickForDropdown) {
            document.removeEventListener('click', this.handleDocumentClickForDropdown, { capture: true });
        }
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Navigates to a specific page number.
     * @param {number} pageNum The page number to navigate to.
     * 
     * @memberof PdfViewer
     */
    handleGoToPage = async (pageNum) => {
        if (this.hasRenderableSource() && pageNum >= 1 && pageNum <= this.totalPages && pageNum !== this.currentPage) {
            this.currentPage = pageNum;

            // Show loader only when rendering a new page.
            this.toggleLoader(true);

            await this.#queueRenderPage(pageNum);
        }
    };

    /**
     * Handles page changes from the content pager component.
     * @param {CustomEvent} event The page-changed event from content-pager.
     * 
     * @memberof PdfViewer
     */
    handlePageChanged = async (event) => {
        const { currentPage } = event.detail;

        await this.handleGoToPage(currentPage);
    };

    /**
     * Handles keyboard zoom shortcuts (Ctrl + '+', Ctrl + '-', Ctrl + '0').
     * @param {KeyboardEvent} event The keyboard event.
     * 
     * @memberof PdfViewer
     */
    handleKeyboardZoom = async (event) => {
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable) {
            return;
        }

        if (!event.ctrlKey) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (event.key === '=' || event.key === '+') {
            await this.handleZoomIn();
        }
        else if (event.key === '-') {
            await this.handleZoomOut();
        }
        else if (event.key === '0') {
            if (this.#panzoom) {
                this.#panzoom.reset();

                // ApplyZoom is async.
                await this.#applyZoom(1);
            }
        }
    };

    /**
     * Handle resize events.
     * Updates page numbers and ensures proper sizing of canvas elements.
     * 
     * @memberof PdfViewer
     */
    handleResize = () => {
        if (this.hasRenderableSource()) {
            // It's important that applyZoom is called, which then calls getValidScale and debouncedRender.
            // This will re-evaluate and re-render.
            this.#applyZoom(this.currentScale);
        }
    };

    /**
     * Handles zoom in button click.
     * @param {Event} event The event data.
     * 
     * @memberof PdfViewer
     */
    handleZoomIn = async (event) => {
        if (!this.#panzoom) {
            return;
        }

        event.stopPropagation();

        const currentPanzoomScale = this.#panzoom.getScale();

        /* eslint-disable-next-line no-magic-numbers -- This is a zoom increase value. */
        await this.#applyZoom(currentPanzoomScale + 0.1);
    };

    /**
     * Handles zoom out button click.
     * @param {Event} event The event data.
     * 
     * @memberof PdfViewer
     */
    handleZoomOut = async (event) => {
        if (!this.#panzoom) {
            return;
        }

        event.stopPropagation();

        const currentPanzoomScale = this.#panzoom.getScale();

        /* eslint-disable-next-line no-magic-numbers -- This is a zoom decrease value. */
        await this.#applyZoom(currentPanzoomScale - 0.1);
    };

    /**
     * Handles zoom level changes from the drop down or input.
     * @param {string} value The zoom level value (e.g., 'page-fit', 'page-width', '0.75')
     * 
     * @memberof PdfViewer
     */
    handleZoomSelectChange = async (value) => {
        if (!this.canvasContainer || !this.hasRenderableSource()) {
            return;
        }

        // Populate the viewport cache for the PDF.js path; an alternate source (e.g. a tile pyramid) populates it from its own render.
        await this.#ensurePageCachePopulated(this.currentPage);

        const cache = this.#pageViewportCache.get(this.currentPage);

        if (!cache) {
            return;
        }

        const { baseHeight, baseWidth } = cache;
        const containerHeight = this.canvasContainer.clientHeight;
        const containerWidth = this.canvasContainer.clientWidth;

        let newScale;

        switch (value) {

            case 'page-fit':
                // Prevent division by zero.
                if (baseWidth === 0 || baseHeight === 0) {
                    newScale = 1;

                    break;
                }

                newScale = Math.min(containerWidth / baseWidth, containerHeight / baseHeight);

                break;

            case 'page-width':
                // Prevent division by zero.
                if (baseWidth === 0) {
                    newScale = 1;

                    break;
                }

                newScale = containerWidth / baseWidth;

                break;

            default:
                newScale = parseFloat(value);
                if (isNaN(newScale)) {
                    // If parsing fails, perhaps reset to current scale or a default.
                    this.updateZoomControlValue(this.currentScale);

                    return;
                }

                break;

        }

        await this.#applyZoom(newScale);
    };
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Renders the specified page onto the canvas.
     * @param {number} pageNum Page number to render
     * 
     * @memberof PdfViewer
     */
    async renderPage(pageNum) {
        if (this.#pageRendering) {
            this.#pageNumberPending = pageNum;

            return;
        }

        this.#pageRendering = true;
        this.currentPage = pageNum;

        // The previous overlay is for the prior page/scale; hide it now so the user doesn't see stale sharp content layered over the new base render.
        this.#abortDetailRender();
        this.#hideDetailOverlay();

        try {
            await this.renderActivePage(pageNum);
        }
        catch (error) {
            window.isDebugMode && console.error('Error rendering page:', pageNum, error);
        }
        finally {
            this.#pageRendering = false;
            this.toggleLoader(false);
        }

        // Re-render if another render was requested during this one (page change or scale change on the same page).
        if (this.#pageNumberPending !== null) {
            const pendingPage = this.#pageNumberPending;

            this.#pageNumberPending = null;
            await this.renderPage(pendingPage);
        }
    }

    /**
     * Renders the active source for the given page. This is the render-strategy seam: the base implementation paints the PDF.js base canvas plus the
     * viewport-detail overlay. A subclass that drives the canvas from another source (e.g. a server tile pyramid) overrides this. `renderPage` owns the
     * re-entrancy guard, loader, and pending-page handling around it, so an override only supplies the per-source painting.
     * @param {number} pageNum One-based page number to render.
     * @returns {Promise<void>} Resolves once the page has been painted.
     *
     * @memberof PdfViewer
     * @protected
     */
    async renderActivePage(pageNum) {
        if (!this.#pdfDoc) {
            return;
        }

        const page = await this.#pdfDoc.getPage(pageNum);

        if (!this.#pageViewportCache.has(pageNum)) {
            const viewport = page.getViewport({ scale: 1 });

            this.cachePageViewport(pageNum, viewport.width, viewport.height);
        }

        const pixelRatio = window.devicePixelRatio || 1;
        const cacheEntry = this.#pageViewportCache.get(pageNum);

        // Mozilla-style base cap.
        // Render the BASE canvas at a baseline scale(fit - page × `BASE_OVERSAMPLE`) — never the full requested zoom.
        // Beyond the baseline, the detail overlay handles sharpness for the visible viewport, so re-rasterising the entire blueprint at every
        // zoom step is unnecessary work.
        // On a phone this avoids re-allocating ~10–13 MP bitmaps per pinch step.
        const containerWidth = this.canvasContainer?.clientWidth || cacheEntry.baseWidth;
        const containerHeight = this.canvasContainer?.clientHeight || cacheEntry.baseHeight;
        const fitScale = Math.min(containerWidth / cacheEntry.baseWidth, containerHeight / cacheEntry.baseHeight) || 1;
        const targetBaseScale = Math.min(fitScale * PdfViewer.BASE_OVERSAMPLE, cacheEntry.maxRenderScale);
        const renderScale = Math.min(this.currentScale, targetBaseScale);

        // Skip the page.render entirely when the existing base bitmap already covers this scale for this page.
        // The user-facing CSS sizing is driven by `updateDimensionsOnly` during pinch and the detail overlay is scheduled below either way,
        // so the on-screen result still updates correctly — we just avoid the costly full-page rasterisation.
        const baseUnchanged = this.#lastBaseRenderPage === pageNum
            && this.#lastBaseRenderScale !== null
            && Math.abs(renderScale - this.#lastBaseRenderScale) < 0.01;

        if (!baseUnchanged) {
            await this.#renderBaseCanvas(page, cacheEntry, renderScale, pixelRatio);

            this.#lastBaseRenderPage = pageNum;
            this.#lastBaseRenderScale = renderScale;
        }

        this.updateContentPager();
        this.updateZoomControlValue(this.currentScale);

        // Schedule the viewport-only sharp overlay; cheap to skip when `currentScale ≤ renderScale` so it's safe to call unconditionally.
        this.#scheduleDetailOverlay(0);

        // Dispatch an event when page is rendered.
        this.dispatchEvent(new CustomEvent('page-rendered', {
            detail: {
                pageNumber: pageNum,
                scale: this.currentScale
            }
        }));
    }

    /**
     * Reports whether the viewer currently has something to render. The base implementation is true once a PDF.js document is loaded; a subclass with
     * an alternate source (e.g. a tile pyramid that keeps the PDF.js document null) widens this so the shared zoom/resize/render plumbing stays active.
     * @returns {boolean} True when a page can be rendered.
     *
     * @memberof PdfViewer
     * @protected
     */
    hasRenderableSource() {
        return !!this.#pdfDoc;
    }

    /**
     * Render-strategy seam invoked by the loader before the PDF.js path. A subclass returns true to claim the source and fully own the load (including
     * any asynchronous generation/polling and its own fallback); the base implementation returns false so the caller proceeds with PDF.js.
     * @param {string} src The source URL being loaded.
     * @returns {Promise<boolean>} True when the subclass has taken over the load.
     *
     * @memberof PdfViewer
     * @protected
     */
    async loadSource(src) {
        void src;

        return false;
    }

    /**
     * Refreshes the on-screen render after a pan/zoom gesture settles. The base implementation re-renders the base canvas on a scale change, or repaints
     * the detail overlay on a pure pan. A subclass with an alternate source overrides this to refresh its own viewport.
     * @param {boolean} scaleChanged True when the gesture changed the scale (vs a pure pan).
     *
     * @memberof PdfViewer
     * @protected
     */
    refreshViewport(scaleChanged) {
        if (scaleChanged) {
            // The full re-render schedules its own detail-overlay refresh at the end, so no extra schedule here.
            this.#debouncedRender(PdfViewer.DEBOUNCE_DELAY);
        }
        else {
            // Pan-only: the base canvas is still valid but the visible region has shifted, so the detail overlay needs to repaint a fresh viewport.
            this.#scheduleDetailOverlay(PdfViewer.DEBOUNCE_DELAY);
        }
    }

    /**
     * Tears down any subclass-owned source (timers, layers, network polling). Called on teardown and whenever the viewer falls back to the PDF.js path.
     * The base implementation is a no-op.
     *
     * @memberof PdfViewer
     * @protected
     */
    teardownSource() {
        // No subclass source by default.
    }

    /**
     * Shows or hides the base PDF.js canvas. A subclass that paints from another source (e.g. tiles) hides the canvas while its own layers are visible.
     * @param {boolean} visible True to show the base canvas, false to hide it.
     *
     * @memberof PdfViewer
     * @protected
     */
    setBaseCanvasVisible(visible) {
        if (this.#visibleCanvas) {
            this.#visibleCanvas.style.display = visible ? '' : 'none';
        }
    }

    /**
     * Clears the page-viewport cache. Exposed for subclasses that repopulate it from their own source metadata on (re)load.
     *
     * @memberof PdfViewer
     * @protected
     */
    clearPageViewportCache() {
        this.#pageViewportCache.clear();
    }

    /**
     * Returns a page's unscaled (scale=1) base dimensions from the viewport cache. Populated by both render paths — PDF.js (`renderActivePage`) and
     * the tile pyramid (`cachePageViewport` in `#renderPageFromTiles`) — so callers get the page's logical size regardless of the active source.
     * @param {number} pageNum One-based page number.
     * @returns {?{baseWidth: number, baseHeight: number}} The base dimensions, or null when the page has not been cached yet.
     *
     * @memberof PdfViewer
     * @protected
     */
    getPageBaseSize(pageNum) {
        const entry = this.#pageViewportCache.get(pageNum);

        return entry ? { baseHeight: entry.baseHeight, baseWidth: entry.baseWidth } : null;
    }

    /**
     * Renders the BASE canvas for the given page at `renderScale × pixelRatio` and swaps it into view. CSS sizing reflects the user-facing
     * `currentScale` so the bitmap is browser-stretched whenever the user zoomed past the base; the detail overlay paints the sharp viewport on top.
     * Extracted from `renderPage` so the skip-when-unchanged check there stays readable.
     * @param {object} page The PDF.js page proxy.
     * @param {{baseWidth: number, baseHeight: number}} cacheEntry Page-cache entry with viewport dimensions.
     * @param {number} renderScale Scale at which to rasterise the page (already capped by `targetBaseScale` and `maxRenderScale`).
     * @param {number} pixelRatio Effective device pixel ratio.
     * @returns {Promise<void>} Resolves when the bitmap has been swapped onto the visible canvas.
     *
     * @memberof PdfViewer
     * @private
     */
    async #renderBaseCanvas(page, cacheEntry, renderScale, pixelRatio) {
        const renderViewport = page.getViewport({ scale: renderScale });
        const cssWidth = cacheEntry.baseWidth * this.currentScale;
        const cssHeight = cacheEntry.baseHeight * this.currentScale;

        this.#hiddenCanvas.width = Math.max(1, Math.floor(renderViewport.width * pixelRatio));
        this.#hiddenCanvas.height = Math.max(1, Math.floor(renderViewport.height * pixelRatio));
        this.#hiddenCanvas.style.inlineSize = `${cssWidth}px`;
        this.#hiddenCanvas.style.blockSize = `${cssHeight}px`;
        this.#hiddenCanvas.dataset.scale = this.currentScale.toString();
        this.#hiddenCanvas.dataset.renderScale = renderScale.toString();

        const hiddenCtx = this.#hiddenCanvas.getContext('2d', { alpha: false });

        hiddenCtx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        hiddenCtx.imageSmoothingEnabled = this.#highres;

        if (this.#highres) {
            hiddenCtx.imageSmoothingQuality = 'high';
        }

        // Fill with white background to prevent black edges when PDF has transparent areas.
        hiddenCtx.fillStyle = '#ffffff';
        hiddenCtx.fillRect(0, 0, renderViewport.width, renderViewport.height);

        // Only size the wrapper before panzoom takes over; afterward, writing the render-scale size here would shrink the wrapper back to a stale
        // scale if the user zoomed during this render.
        // Panzoom owns wrapper sizing via `updateDimensionsOnly` after init.
        if (!this.#panzoom) {
            this.wrapper.style.inlineSize = `${cssWidth}px`;
            this.wrapper.style.blockSize = `${cssHeight}px`;
        }

        await page.render({
            canvasContext: hiddenCtx,
            viewport: renderViewport
        }).promise;

        this.#swapCanvases();
    }

    /**
     * Updates only the dimensions of the visible canvas and wrapper based on the current scale.
     * This is used during zoom operations (specifically by panzoomzoom event) to avoid full re-renders.
     * 
     * @memberof PdfViewer
     */
    updateDimensionsOnly() {
        if (!this.hasRenderableSource() || !this.#pageViewportCache.has(this.currentPage)) {
            return;
        }

        const pageCache = this.#pageViewportCache.get(this.currentPage);
        if (!pageCache || typeof pageCache.baseWidth === 'undefined' || typeof pageCache.baseHeight === 'undefined') {
            // This can happen if renderPage hasn't completed for the current page yet.
            // Attempt to get viewport directly, but this requires an async call.
            // For a synchronous update, we might need to ensure baseWidth/Height are always available or skip if not.
            return;
        }

        const { baseWidth, baseHeight } = pageCache;
        const newWidth = baseWidth * this.currentScale;
        const newHeight = baseHeight * this.currentScale;

        this.#visibleCanvas.style.inlineSize = `${newWidth}px`;
        this.#visibleCanvas.style.blockSize = `${newHeight}px`;

        this.wrapper.style.inlineSize = `${newWidth}px`;
        this.wrapper.style.blockSize = `${newHeight}px`;

        // The detail overlay was rendered for the previous scale and would now appear at the wrong PDF region (its CSS box is in wrapper-local
        // coordinates). Hide it during the gesture; the panzoomend handler schedules a fresh overlay render once the scale settles.
        this.#hideDetailOverlay();
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Applies zoom at the specified scale and updates UI.
     * @param {number} scale The scale to apply.
     * @param {object} [options] Optional zoom options for Panzoom.
     * @param {object} [options.focal] Focal point for zooming.
     * 
     * @memberof PdfViewer
     * @private
     */
    async #applyZoom(scale, options = {}) {
        if (!this.hasRenderableSource()) {
            return;
        }

        // If panzoom isn't ready (e.g. during initial load), queue this or handle gracefully.
        if (!this.#panzoom) {
            // It might be an initial call before setupPanzoom completes.
            // For now, we'll just set currentScale and the debouncedRender will pick it up once panzoom is initialized and first render is done.
            this.currentScale = this.getValidScale(scale);
            this.updateZoomControlValue(this.currentScale);
            this.#debouncedRender(PdfViewer.DEBOUNCE_DELAY);

            return;
        }

        this.currentScale = this.getValidScale(scale);

        if (!options.focal && this.wrapper) {
            const rect = this.wrapper.getBoundingClientRect();

            options.focal = {
                clientX: rect.left + (rect.width / 2),
                clientY: rect.top + (rect.height / 2)
            };
        }

        // Panzoom handles the visual transform.
        this.#panzoom.zoom(this.currentScale, options);

        // Update the input display.
        this.updateZoomControlValue(this.currentScale);

        // Debounced render to update canvas content at new scale.
        this.#debouncedRender(PdfViewer.DEBOUNCE_DELAY);
    }

    /**
     * Creates and configures the canvas container and canvas elements (visible and hidden).
     * 
     * @memberof PdfViewer
     * @private
     */
    #createCanvasElements() {
        this.canvasContainer = document.createElement('div');
        this.canvasContainer.className = 'canvas-container';

        this.wrapper = document.createElement('div');
        this.wrapper.className = 'pdf-canvas-wrapper';

        this.#visibleCanvas = document.createElement('canvas');
        this.#visibleCanvas.className = 'pdf-canvas';

        // ARIA: Describe the canvas for screen readers.
        this.#visibleCanvas.setAttribute('role', 'document');
        this.#visibleCanvas.setAttribute('aria-label', 'PDF page content');

        this.#hiddenCanvas = document.createElement('canvas');
        this.#hiddenCanvas.className = 'pdf-hidden-canvas';
        this.#hiddenCanvas.style.display = 'none';
        this.#hiddenCanvas.setAttribute('aria-hidden', 'true');

        // Detail-view overlay canvas. Lives inside the wrapper so panzoom's `inset*Start` translations move it together with the base canvas, keeping it
        // anchored to the same PDF region while the user pans. Hidden until `#renderDetailOverlay` paints into it.
        this.#detailCanvas = document.createElement('canvas');
        this.#detailCanvas.className = 'pdf-detail-canvas';
        this.#detailCanvas.hidden = true;
        this.#detailCanvas.setAttribute('aria-hidden', 'true');

        this.wrapper.append(this.#visibleCanvas, this.#detailCanvas);
        this.canvasContainer.appendChild(this.wrapper);
        this.appendChild(this.canvasContainer);

        // Append hidden canvas to DOM but keep it hidden for rendering.
        this.appendChild(this.#hiddenCanvas);
    }

    /**
     * Creates reset button for resetting zoom and position.
     * @returns {HTMLButtonElement} The reset button
     * 
     * @memberof PdfViewer
     * @private
     */
    #createResetButton() {
        const resetButton = document.createElement('button');
        resetButton.className = 'button button-tall zoom-reset';

        const resetLabel = this.#locales?.resetZoom || 'Zoom resetten';
        resetButton.setAttribute('aria-label', resetLabel);
        resetButton.setAttribute('title', resetLabel);
        resetButton.innerHTML = `<span class="tool-bar-icon lf-icon-undo" aria-hidden="true"></span><span class="visually-hidden">${resetLabel}</span>`;

        resetButton.addEventListener('click', async (event) => {
            event.stopPropagation();

            if (this.#panzoom) {
                this.#panzoom.reset();

                await this.#applyZoom(1);
            }
        }, { capture: false });

        return resetButton;
    }

    /**
     * Creates zoom buttons container with zoom in/out buttons.
     * @returns {HTMLDivElement} The zoom buttons container.
     * 
     * @memberof PdfViewer
     * @private
     */
    #createZoomButtonsContainer() {
        const container = document.createElement('div');
        container.className = 'zoom-buttons-container';

        const zoomOutButton = document.createElement('button');
        zoomOutButton.className = 'zoom-button zoom-out';

        const zoomOutLabel = this.#locales?.zoomOut || 'Uitzoomen';
        zoomOutButton.setAttribute('aria-label', zoomOutLabel);
        zoomOutButton.setAttribute('title', zoomOutLabel);
        zoomOutButton.innerHTML = `<span class="tool-bar-icon lf-icon-zoom-out" aria-hidden="true"></span><span class="visually-hidden">${zoomOutLabel}</span>`;
        zoomOutButton.addEventListener('click', this.handleZoomOut, { capture: false });

        const buttonSeparator = document.createElement('div');
        buttonSeparator.className = 'zoom-button-separator';

        const zoomInButton = document.createElement('button');
        zoomInButton.className = 'zoom-button zoom-in';

        const zoomInLabel = this.#locales?.zoomIn || 'Inzoomen';
        zoomInButton.setAttribute('aria-label', zoomInLabel);
        zoomInButton.setAttribute('title', zoomInLabel);
        zoomInButton.innerHTML = `<span class="tool-bar-icon lf-icon-zoom-in" aria-hidden="true"></span><span class="visually-hidden">${zoomInLabel}</span>`;
        zoomInButton.addEventListener('click', this.handleZoomIn, { capture: false });

        container.append(zoomOutButton, buttonSeparator, zoomInButton);

        return container;
    }

    /**
     * Creates zoom select container with custom input and dropdown functionality.
     * @returns {HTMLDivElement} The zoom select container.
     * 
     * @memberof PdfViewer
     * @private
     */
    #createZoomSelectContainer() {
        const container = document.createElement('div');
        container.className = 'zoom-select-container';

        const inputWrapper = document.createElement('div');
        inputWrapper.className = 'zoom-input-wrapper';

        const zoomInput = document.createElement('input');
        zoomInput.className = 'zoom-input';
        zoomInput.type = 'text';

        const zoomLevelLabel = resources?.pdfViewer?.zoomLevel || 'Zoom Level';

        zoomInput.setAttribute('aria-label', zoomLevelLabel);
        zoomInput.setAttribute('title', zoomLevelLabel);

        const dropdownButton = document.createElement('button');
        const selectZoomLabel = this.#locales?.selectZoomOptions || 'Zoom opties selecteren';
        dropdownButton.className = 'zoom-dropdown-button';
        dropdownButton.innerHTML = `<span class="tool-bar-icon lf-icon-arrow-drop-down" aria-hidden="true"></span><span class="visually-hidden">${selectZoomLabel}</span>`;
        dropdownButton.setAttribute('aria-label', selectZoomLabel);
        dropdownButton.setAttribute('title', selectZoomLabel);
        dropdownButton.setAttribute('aria-haspopup', 'true');
        dropdownButton.setAttribute('aria-expanded', 'false');

        const dropdownContent = document.createElement('div');

        dropdownContent.className = 'zoom-dropdown-content';
        dropdownContent.setAttribute('role', 'listbox');

        this.#zoomLevelDefinitions.filter((level) => {
            return level.isSelectOption;
        }).forEach((level) => {
            const option = document.createElement('div');

            option.className = 'zoom-option';
            option.dataset.value = level.value;
            option.setAttribute('role', 'option');
            option.tabIndex = -1;

            let labelText = level.label;

            if (level.labelKey && resources && resources.pdfViewer) {
                labelText = resources.pdfViewer[level.labelKey] || level.label || level.value;
            }
            else if (level.labelKey) {
                // Fallback if resources not present.
                labelText = level.label || level.value;
            }

            option.textContent = labelText;

            option.addEventListener('click', async () => {
                // Set input to the friendly label.
                zoomInput.value = labelText;
                dropdownContent.classList.remove('show');
                dropdownButton.setAttribute('aria-expanded', 'false');

                await this.handleZoomSelectChange(level.value);
            }, { capture: false });

            option.addEventListener('keydown', async (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();

                    zoomInput.value = labelText;
                    dropdownContent.classList.remove('show');
                    dropdownButton.setAttribute('aria-expanded', 'false');

                    await this.handleZoomSelectChange(level.value);

                    // Return focus to input or button.
                    zoomInput.focus();
                }
            });

            dropdownContent.appendChild(option);
        });

        dropdownButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const isShown = dropdownContent.classList.toggle('show');

            dropdownButton.setAttribute('aria-expanded', isShown.toString());
        }, { capture: false });

        // Use a bound handler for document click to be able to remove it later if needed.
        this.handleDocumentClickForDropdown = (e) => {
            if (!inputWrapper.contains(e.target)) {
                dropdownContent.classList.remove('show');
                dropdownButton.setAttribute('aria-expanded', 'false');
            }
        };

        document.addEventListener('click', this.handleDocumentClickForDropdown, { capture: true });

        zoomInput.addEventListener('click', (e) => {
            return e.stopPropagation();
        }, { capture: false });

        zoomInput.addEventListener('mousedown', (e) => {
            return e.stopPropagation();
        }, { capture: false });

        zoomInput.addEventListener('keydown', async (e) => {
            if (dropdownContent.classList.contains('show')) {
                dropdownContent.classList.remove('show');
                dropdownButton.setAttribute('aria-expanded', 'false');
            }

            if (e.key === 'Enter') {
                e.preventDefault();

                let value = zoomInput.value.trim();

                if (value.endsWith('%')) {
                    value = value.slice(0, -1).trim();
                }

                const numValue = parseFloat(value) / 100;

                if (!isNaN(numValue) && numValue >= this.#minScale && numValue <= this.#maxScale) {
                    await this.handleZoomSelectChange(numValue.toString());
                    zoomInput.blur();
                }
                else {
                    // Reset to current valid scale's display.
                    this.updateZoomControlValue(this.currentScale);
                }
            }
        }, { capture: false });

        zoomInput.addEventListener('blur', async () => {
            // Delay processing blur slightly to allow click on dropdown options.
            setTimeout(async () => {
                if (document.activeElement === dropdownButton || dropdownContent.contains(document.activeElement)) {
                    // Don't process blur if focus moved to dropdown parts.
                    return;
                }

                let value = zoomInput.value.trim();
                const predefinedLevel = this.#zoomLevelDefinitions.find((level) => {
                    return (level.labelKey ? (resources?.pdfViewer?.[level.labelKey] || level.label) : level.label) === value;
                });

                if (predefinedLevel && predefinedLevel.dynamic) {
                    // For dynamic levels like "Page Fit", their actual scale value can change.
                    // Re-evaluate and update the display based on the currentScale achieved by that dynamic level.
                    // This means if user types "Page Fit", it should resolve to current scale if it was set by "Page Fit"
                    // Or, if they type it and it's not the current mode, it should trigger handleZoomSelectChange.
                    // For simplicity on blur, if it's a dynamic label, we just update the display to the current numeric scale.
                    this.updateZoomControlValue(this.currentScale);
                }
                else {
                    if (value.endsWith('%')) {
                        value = value.slice(0, -1).trim();
                    }

                    const numValue = parseFloat(value) / 100;

                    if (isNaN(numValue)) {
                        this.updateZoomControlValue(this.currentScale);

                        return;
                    }

                    // Check if the value represents a meaningful change within valid bounds.
                    const isSignificantChange = Math.abs(numValue - this.currentScale) > PdfViewer.SCALE_TRESHOLD;
                    const isWithinBounds = numValue >= this.#minScale && numValue <= this.#maxScale;

                    if (isSignificantChange && isWithinBounds) {
                        await this.handleZoomSelectChange(numValue.toString());
                    }
                    else {
                        // Revert to current scale display for insignificant changes or out-of-bounds values.
                        this.updateZoomControlValue(this.currentScale);
                    }
                }
            }, 100);
        }, { capture: false });

        inputWrapper.append(zoomInput, dropdownButton, dropdownContent);
        container.appendChild(inputWrapper);

        this.#zoomControl = zoomInput;

        return container;
    }

    /**
     * Manages delayed rendering with debouncing.
     * Clears any existing timeout and sets a new one to render the current page.
     * @param {number} delay Delay in milliseconds before rendering.
     * 
     * @memberof PdfViewer
     * @private
     */
    #debouncedRender(delay) {
        clearTimeout(this.#renderTimeout);

        this.#renderTimeout = setTimeout(async () => {
            if (this.hasRenderableSource()) {
                await this.#queueRenderPage(this.currentPage);
            }
        }, delay);
    }

    /**
     * Ensures the viewport cache is populated for the given page.
     * @param {number} pageNum The page number to ensure cache for.
     * 
     * @memberof PdfViewer
     * @private
     */
    async #ensurePageCachePopulated(pageNum) {
        if (this.#pageViewportCache.has(pageNum) || !this.#pdfDoc) {
            return;
        }

        try {
            const page = await this.#pdfDoc.getPage(pageNum);
            const viewport = page.getViewport({ scale: 1 });

            this.cachePageViewport(pageNum, viewport.width, viewport.height);
        }
        catch (error) {
            window.isDebugMode && console.warn(`Failed to populate cache for page ${pageNum}:`, error);
        }
    }

    /**
     * Stores a page's viewport metrics in the cache. `maxRenderScale` is the largest scale at which `baseW × baseH × scale² × pixelRatio²` still fits
     * within `MAX_CANVAS_PIXELS` — the base canvas is capped at this value, while the user-facing zoom can go higher (the detail overlay restores
     * sharpness in the visible region).
     * @param {number} pageNum One-based page number.
     * @param {number} baseWidth Page width in PDF points (scale=1 viewport width).
     * @param {number} baseHeight Page height in PDF points.
     *
     * @memberof PdfViewer
     * @private
     */
    cachePageViewport(pageNum, baseWidth, baseHeight) {
        const pixelRatio = window.devicePixelRatio || 1;
        const area = Math.max(1, baseWidth * baseHeight);
        const maxRenderScale = Math.sqrt(PdfViewer.MAX_CANVAS_PIXELS / area) / pixelRatio;

        this.#pageViewportCache.set(pageNum, {
            baseHeight,
            baseWidth,
            maxRenderScale
        });
    }

    /**
     * Ensures that the PDF.js library is loaded and ready to use.
     * @returns {Promise} Promise that resolves when PDF.js is ready.
     * 
     * @private
     * @memberof PdfViewer
     */
    async #ensurePdfjsLibLoaded() {
        // Check if pdfjsLib is already available.
        if (typeof pdfjsLib !== 'undefined') {
            // Initialize worker if not already set.
            if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
                pdfjsLib.GlobalWorkerOptions.workerSrc = `${baseUrl}/dist/scripts/pdf.worker.min.mjs?v=${pdfjsLib.version}`;
            }

            return Promise.resolve();
        }

        // Wait for pdfjsLib to become available.
        return new Promise((resolve, reject) => {
            let attempts = 0;

            // 10 seconds max (100 * 100ms).
            const maxAttempts = 100;

            const checkForPdfjsLib = () => {
                attempts++;

                if (typeof pdfjsLib !== 'undefined') {
                    // Initialize worker.
                    pdfjsLib.GlobalWorkerOptions.workerSrc = `${baseUrl}/dist/scripts/pdf.worker.min.mjs?v=${pdfjsLib.version}`;

                    resolve();
                }
                else if (attempts >= maxAttempts) {
                    reject(new Error('PDF.js library failed to load within timeout period'));
                }
                else {
                    setTimeout(checkForPdfjsLib, 100);
                }
            };

            checkForPdfjsLib();
        });
    }

    /**
     * Checks the scale against the max pixels allowed and component's min/max scale.
     * @param {number} scale The scale to check.
     * @returns {number} The validated scale, clamped within allowed boundaries.
     * 
     * @memberof PdfViewer
     * @private
     */
    getValidScale(scale) {
        const pageCacheEntry = this.#pageViewportCache.get(this.currentPage);

        // PDF.js mode: schedule cache population for next tick when the cache is missing.
        // An alternate source (e.g. a tile pyramid) populates the cache from its own render, so a resize before that first
        // render must fall through to default limits without crashing.
        if (!pageCacheEntry && this.#pdfDoc) {
            setTimeout(() => {
                return this.#ensurePageCachePopulated(this.currentPage);
            }, 0);
        }

        // Cap the user-facing zoom strictly by `this.#maxScale`. The base canvas is independently capped at `maxRenderScale` inside `renderPage`,
        // and the detail overlay covers the resulting CSS-stretching by re-rasterising the visible region — so there's no longer a reason to refuse
        // zoom levels above the canvas-pixel cap.
        const clamped = Math.max(this.#minScale, Math.min(this.#maxScale, scale));

        this.classList.toggle('at-max-zoom', clamped >= this.#maxScale);
        this.#syncPanzoomMaxScale(this.#maxScale);

        return clamped;
    }

    /**
     * Pushes a new `maxScale` into Panzoom only when it actually changed. The continuous panzoomzoom stream calls `getValidScale` every frame, so
     * dedupe to keep the inner option-merge cheap.
     * @param {number} maxScale Effective maximum scale to apply.
     *
     * @memberof PdfViewer
     * @private
     */
    #syncPanzoomMaxScale(maxScale) {
        if (!this.#panzoom || this.#lastEffectiveMaxScale === maxScale) {
            return;
        }

        this.#panzoom.setOptions({ maxScale });
        this.#lastEffectiveMaxScale = maxScale;
    }

    /**
     * Loads the source either from the server-side tile pyramid (when the source carries a blueprint id) or via PDF.js. Tile mode is preferred; a
     * transport failure or a `failed` status falls back to PDF.js transparently.
     * @param {string} src URL of the PDF file.
     *
     * @memberof PdfViewer
     * @private
     */
    async #loadPDF(src) {
        // Cancel any in-flight detail render and hide a stale overlay before routing the new source.
        this.#abortDetailRender();
        this.#hideDetailOverlay();

        // Clear the previous render-mode label so an observer can't see stale "tile/viewport" text while the new source is being routed.
        this.#renderMode = null;
        this.removeAttribute('data-render-mode');

        // Forget the prior base-render bookkeeping so the new source forces a full first render — without this, a same-scale skip could keep the old
        // bitmap on screen when switching documents.
        this.#lastBaseRenderPage = null;
        this.#lastBaseRenderScale = null;

        // Give a subclass first refusal on the source (e.g. a server-side tile pyramid). When it returns true it fully owns the load — including any
        // asynchronous generation/poll or its own fallback — so we stop here. Otherwise fall through to the PDF.js path.
        if (await this.loadSource(src)) {
            return;
        }

        await this.loadWithPdfjs(src);
    }

    /**
     * Loads the source via PDF.js (legacy path / tile-mode fallback).
     * @param {string} src URL of the PDF file.
     *
     * @memberof PdfViewer
     * @private
     */
    async loadWithPdfjs(src) {
        // Mark the mode at the entry point: every code path that lands here (initial load without `blueprint-id`, manifest failure, or 'failed'
        // status) is rendering through the PDF.js + viewport-detail-overlay stack.
        if (src) {
            this.setRenderMode('viewport');
        }

        // PDF.js renders to the canvas — make sure any subclass source (e.g. tile layers) is torn down and the base canvas is visible (a prior
        // tile-mode source may have hidden it).
        this.teardownSource();
        this.setBaseCanvasVisible(true);

        if (!src) {
            // Clear current PDF and show error.
            this.totalPages = 0;

            // Clean up previous doc.
            if (this.#pdfDoc) {
                await this.#pdfDoc.destroy();
                this.#pdfDoc = null;
            }

            this.#pdfDoc = null;
            this.updateContentPager();
            this.toggleLoader(false);

            if (this.navigation) {
                this.navigation.classList.add('visible');
            }

            return;
        }

        this.toggleLoader(true);

        try {
            // Ensure PDF.js library is loaded before proceeding.
            await this.#pdfjsLibReady;

            // Destroy previous PDF document if it exists to free resources.
            if (this.#pdfDoc) {
                await this.#pdfDoc.destroy();
                this.#pdfDoc = null;
            }

            // Destroy panzoom before loading new PDF and setting up new one.
            this.destroyPanzoom();

            const loadingTask = pdfjsLib.getDocument(src);

            this.#pdfDoc = await loadingTask.promise;
            this.totalPages = this.#pdfDoc.numPages;
            this.currentPage = 1;
            this.#pageViewportCache.clear();

            // Reset scale.
            this.currentScale = 1;

            // Initial render of the first page. This populates cache for page 1.
            await this.renderPage(this.currentPage);

            // Setup Panzoom after the first render ensures wrapper has dimensions.
            if (!this.#panzoom) {
                this.setupPanzoom();
            }

            // Apply initial zoom (e.g., page-fit) AFTER panzoom is set up.
            // The renderPage above uses currentScale=1. Now apply the desired fit.
            await this.handleZoomSelectChange('page-fit');

            if (this.navigation) {
                this.navigation.classList.add('visible');
            }

            this.updateContentPager();

            this.dispatchEvent(new CustomEvent('pdf-loaded', { detail: { numPages: this.totalPages } }));
        }
        catch (error) {
            window.isDebugMode && console.error('Error loading PDF:', src, error);

            // Reset total pages on error.
            this.totalPages = 0;

            // Update content pager based on error state.
            this.updateContentPager();

            if (this.navigation) {
                this.navigation.classList.add('visible');
            }
        }
        finally {
            this.toggleLoader(false);
        }
    }

    /**
     * Queues the page rendering to prevent multiple simultaneous renders.
     * If a page is already rendering, the new page number is stored as pending.
     * @param {number} pageNum Page number to render.
     * 
     * @memberof PdfViewer
     * @private
     */
    async #queueRenderPage(pageNum) {
        if (this.#pageRendering) {
            this.#pageNumberPending = pageNum;
        }
        else {
            await this.renderPage(pageNum);
        }
    }

    /**
     * Sets up the navigation controls using the content-pager component.
     * 
     * @memberof PdfViewer
     * @private
     */
    #setupNavigation() {
        // Create the content-pager component.
        this.#contentPager = document.createElement('content-pager');
        this.#contentPager.setAttribute('max-visible', this.maxVisible);

        // Listen for page changes from the content pager.
        this.#contentPager.addEventListener('page-changed', this.handlePageChanged);

        this.navigation.appendChild(this.#contentPager);

        this.navigation.style.containerType = 'inline-size';
        this.navigation.style.containerName = 'navigation-container';
        
        // Update with current state.
        this.updateContentPager();
    }

    /**
     * Destroys the current Panzoom instance, if any, so the next `setupPanzoom` rebuilds it. Panzoom captures the
     * wrapper's unscaled size as its `contain` reference at construction, so an instance built for one page keeps that
     * aspect ratio; a differently-shaped page/blueprint must destroy it first or it jumps on the first pan/zoom.
     *
     * @memberof PdfViewer
     * @protected
     */
    destroyPanzoom() {
        if (this.#panzoom) {
            this.#panzoom.destroy();
            this.#panzoom = null;
        }
    }

    /**
     * Setup Panzoom for the canvas wrapper to enable zoom and pan functionalities.
     *
     * @memberof PdfViewer
     * @private
     */
    setupPanzoom() {
        if (!this.wrapper || this.#panzoom) {
            return;
        }

        // Normalize the wrapper to its unscaled (scale=1) page size before Panzoom captures its `originalWidth`/`originalHeight` reference. Panzoom sizes
        // the wrapper as `originalWidth × scale`, while `updateDimensionsOnly` sizes it as `baseWidth × currentScale`; the two only agree when Panzoom's
        // reference equals the unscaled page. PDF.js mode sets up at scale 1 so this already holds, but tile mode renders at the fit scale first — leaving
        // Panzoom with a fit-scaled "original" so its pan/constrain model drifts from the displayed wrapper and the first pan jumps. Sizing to the base
        // here makes both writers compute the identical size in every mode; the constructor's `zoom(startScale)` immediately rescales it to fit, so there
        // is no flash.
        const base = this.getPageBaseSize(this.currentPage);

        if (base) {
            this.wrapper.style.inlineSize = `${base.baseWidth}px`;
            this.wrapper.style.blockSize = `${base.baseHeight}px`;
        }

        // Pin the panzoom maxScale to the user-facing limit. The base canvas independently caps itself at `maxRenderScale` inside `renderPage`,
        // and the detail overlay paints the visible region sharply for any zoom above that cap.
        this.#panzoom = new Panzoom(this.wrapper, {
            contain: true,
            maxScale: this.#maxScale,
            minScale: this.#minScale,
            startScale: this.currentScale,
            step: 0.1,
            useParent: true
        });

        this.wrapper.addEventListener('panzoomzoom', (event) => {
            // Coalesce the high-frequency pinch stream into one rAF tick. Each event still picks up the latest scale, but the layout/DOM writes only
            // run once per frame instead of per-pointer-sample.
            this.#pendingPanzoomScale = event.detail.scale;

            if (this.#zoomFrame !== null) {
                return;
            }

            this.#zoomFrame = requestAnimationFrame(() => {
                this.#zoomFrame = null;

                if (this.#pendingPanzoomScale === null) {
                    return;
                }

                const pendingScale = this.#pendingPanzoomScale;

                this.#pendingPanzoomScale = null;
                this.currentScale = this.getValidScale(pendingScale);
                this.updateZoomControlValue(this.currentScale);
                this.updateDimensionsOnly();
            });
        });

        this.wrapper.addEventListener('panzoompan', () => {
            // Pan emits a continuous stream while the finger/wheel moves; `panzoomend` only fires once it settles. Refreshing solely on end leaves every
            // newly-scrolled-in region on the blurred base layer until the gesture stops. This hook lets a subclass mount fresh content *during* the pan.
            this.refreshViewportDuringPan();
        });

        this.wrapper.addEventListener('panzoomend', (event) => {
            const scaleChanged = Math.abs(event.detail.scale - this.currentScale) > PdfViewer.SCALE_TRESHOLD;

            this.refreshViewport(scaleChanged);
        });
    }

    /**
     * Hook invoked on every `panzoompan` event (i.e. continuously while a pan/scroll gesture is in progress). The base implementation is a no-op: PDF.js
     * mode repaints its detail overlay only when the gesture settles (`refreshViewport`), since a per-frame canvas repaint would be costly. A subclass with
     * a cheap incremental update (e.g. mounting DOM tiles) overrides this to refresh progressively as new regions scroll into view.
     *
     * @memberof PdfViewer
     * @protected
     */
    refreshViewportDuringPan() {
        // No-op by default; see method doc.
    }

    /**
     * Sets up zoom controls (buttons, select dropdown, reset) for the PDF viewer toolbar.
     * 
     * @memberof PdfViewer
     * @private
     */
    #setupZoomControl() {
        const zoomButtonsContainer = this.#createZoomButtonsContainer();
        const zoomSelectContainer = this.#createZoomSelectContainer();
        const resetButton = this.#createResetButton();

        this.toolbar.append(zoomButtonsContainer, zoomSelectContainer, resetButton);

        this.addEventListener('keydown', this.handleKeyboardZoom, { capture: true });

        // Make the component focusable for keyboard events if it's not inherently focusable.
        if (this.getAttribute('tabindex') === null) {
            this.setAttribute('tabindex', '0');
        }

        // Focus handling for keyboard navigation context (optional, depends on desired behavior).
        this.addEventListener('focus', () => {
            this.classList.add('focused-component');
        });

        this.addEventListener('blur', () => {
            this.classList.remove('focused-component');
        });
    }

    /**
     * Swaps the visible and hidden canvases. Draws content from hidden (off-screen rendered) canvas to the visible canvas.
     * 
     * @memberof PdfViewer
     * @private
     */
    #swapCanvases() {
        if (!this.#hiddenCanvas || !this.#visibleCanvas || !this.#pageViewportCache.has(this.currentPage)) {
            return;
        }

        const hiddenWidth = this.#hiddenCanvas.width;
        const hiddenHeight = this.#hiddenCanvas.height;
        const styleWidth = this.#hiddenCanvas.style.inlineSize;
        const styleHeight = this.#hiddenCanvas.style.blockSize;

        this.#visibleCanvas.width = hiddenWidth;
        this.#visibleCanvas.height = hiddenHeight;

        // Track the wrapper's live size (driven by panzoom) so a stale-scale render lands as a CSS-stretched bitmap for one frame, instead of snapping
        // the visible canvas back to the render scale. Falls back to the hidden canvas style for the very first paint where panzoom hasn't run yet.
        this.#visibleCanvas.style.inlineSize = this.wrapper.style.inlineSize || styleWidth;
        this.#visibleCanvas.style.blockSize = this.wrapper.style.blockSize || styleHeight;

        const pageCache = this.#pageViewportCache.get(this.currentPage);

        if (pageCache) {
            this.#visibleCanvas.dataset.baseWidth = pageCache.baseWidth ? pageCache.baseWidth.toString() : '0';
            this.#visibleCanvas.dataset.baseHeight = pageCache.baseHeight ? pageCache.baseHeight.toString() : '0';
        }

        this.#visibleCanvas.dataset.lastScale = this.#hiddenCanvas.dataset.scale || '1';

        const visibleCtx = this.#visibleCanvas.getContext('2d', { alpha: false });

        // Reset transform before drawing.
        visibleCtx.setTransform(1, 0, 0, 1, 0, 0);
        visibleCtx.imageSmoothingEnabled = this.#highres;

        if (this.#highres) {
            visibleCtx.imageSmoothingQuality = 'high';
        }

        // Fill with white background before drawing to ensure no transparency issues.
        visibleCtx.fillStyle = '#ffffff';
        visibleCtx.fillRect(0, 0, this.#visibleCanvas.width, this.#visibleCanvas.height);

        visibleCtx.drawImage(this.#hiddenCanvas, 0, 0);
    }

    /**
     * Toggle loader display.
     * @param {Boolean} show True to show the loader, false (default) to hide.
     * 
     * @memberof PdfViewer
     * @private
     */
    toggleLoader(show = false) {
        // Guard against missing canvasContainer.
        if (!this.canvasContainer) {
            return;
        }

        if (show) {
            this.canvasContainer.classList.add('loading');

            if (this.#loader) {
                this.#loader.style.display = 'block';
            }
        }
        else {
            this.canvasContainer.classList.remove('loading');

            if (this.#loader) {
                this.#loader.style.display = 'none';
            }
        }
    }

    /**
     * Updates the content pager component with current page and total pages.
     * Also updates the page count display.
     * 
     * @memberof PdfViewer
     * @private
     */
    updateContentPager() {
        // Update if we have a content-pager component.
        if (this.#contentPager && this.#contentPager.tagName === 'CONTENT-PAGER') {
            this.#contentPager.setAttribute('current-page', this.currentPage.toString());
            this.#contentPager.setAttribute('total-pages', this.totalPages.toString());
        }
    }

    /**
     * Updates the zoom control input/dropdown to reflect the current scale.
     * @param {number} scale The current scale value.
     * 
     * @memberof PdfViewer
     * @private
     */
    updateZoomControlValue(scale) {
        if (!this.#zoomControl) {
            return;
        }

        const scalePercentage = `${Math.round(scale * 100)}%`;

        // Default to percentage.
        let displayText = scalePercentage;

        // Check if the current scale matches one of the predefined fixed percentage zoom levels.
        for (const level of this.#zoomLevelDefinitions) {
            // Only process non-dynamic levels for direct matching.
            if (!level.dynamic) {
                const numericLevelValue = parseFloat(level.value);

                // Tolerance for float comparison.
                if (!isNaN(numericLevelValue) && Math.abs(numericLevelValue - scale) < PdfViewer.SCALE_TRESHOLD) {
                    let currentLevelLabel = level.label;

                    if (level.labelKey && typeof resources !== 'undefined' && resources.pdfViewer && resources.pdfViewer[level.labelKey]) {
                        currentLevelLabel = resources.pdfViewer[level.labelKey];
                    }
                    else if (level.labelKey) {
                        // Fallback.
                        currentLevelLabel = level.label || level.value;
                    }

                    displayText = currentLevelLabel;

                    break;
                }
            }
        }

        this.#zoomControl.value = displayText;
    }

    /**
     * Records the active render mode and broadcasts it to embedders.
     * Sets the `data-render-mode` attribute (so CSS / DevTools can see it at a glance) and fires a `render-mode` custom event with `{ mode }`.
     * Idempotent — repeated calls with the same value are no-ops, so it's safe to invoke from
     * the multiple branches in the load pipeline that converge on the same final mode.
     * @param {('tile'|'viewport')} mode Active render mode.
     *
     * @memberof PdfViewer
     * @private
     */
    setRenderMode(mode) {
        if (mode !== 'tile' && mode !== 'viewport') {
            return;
        }

        if (this.#renderMode === mode) {
            return;
        }

        this.#renderMode = mode;
        this.setAttribute('data-render-mode', mode);
        this.dispatchEvent(new CustomEvent('render-mode', { bubbles: true, composed: true, detail: { mode } }));
    }

    /**
     * Cancels the in-flight detail-overlay PDF.js render task and any pending debounce, so a new render starts from a clean slate.
     *
     * @memberof PdfViewer
     * @private
     */
    #abortDetailRender() {
        if (this.#detailDebounceTimer !== null) {
            window.clearTimeout(this.#detailDebounceTimer);
            this.#detailDebounceTimer = null;
        }

        if (this.#detailRenderTask) {
            try {
                this.#detailRenderTask.cancel();
            }
            catch {
                // PDF.js raises `RenderingCancelledException` from the awaiter; cancelling an already-finished task is a no-op but the call itself
                // shouldn't ever throw — swallow defensively to keep the abort path noise-free.
            }

            this.#detailRenderTask = null;
        }
    }

    /**
     * Hides the detail-overlay canvas without releasing its backing store. Cheap; called whenever the overlay would otherwise paint a stale viewport
     * (during pinch zoom, between page changes, on PDF teardown).
     *
     * @memberof PdfViewer
     * @private
     */
    #hideDetailOverlay() {
        if (this.#detailCanvas) {
            this.#detailCanvas.hidden = true;
        }
    }

    /**
     * Debounced entry point for detail-overlay rendering. Coalesces rapid pan/zoom events so we only re-rasterise after the gesture settles.
     * @param {number} delay Debounce delay in ms (use 0 for "next microtask").
     *
     * @memberof PdfViewer
     * @private
     */
    #scheduleDetailOverlay(delay) {
        // Detail overlay is exclusive to the PDF.js path; it's gated on the PDF.js document.
        if (!this.#pdfDoc || !this.#detailCanvas) {
            return;
        }

        if (this.#detailDebounceTimer !== null) {
            window.clearTimeout(this.#detailDebounceTimer);
        }

        this.#detailDebounceTimer = window.setTimeout(() => {
            this.#detailDebounceTimer = null;
            this.#renderDetailOverlay(this.currentPage).catch((error) => {
                window.isDebugMode && console.warn('Detail overlay render failed:', error);
            });
        }, Math.max(0, delay));
    }

    /**
     * Renders the currently-visible region of `pageNum` into the detail-overlay canvas at full `currentScale × pixelRatio` resolution.
     * Skips when the base canvas is already at the requested scale (no sharpness benefit) or when there's no visible region (wrapper off-screen).
     *
     * The geometry: the wrapper's CSS box equals `baseW × currentScale × baseH × currentScale` in the unscaled DOM (panzoom resizes via
     * `inlineSize`/`blockSize`, not `transform`), so wrapper-local CSS pixels map 1:1 to PDF-points-at-currentScale.
     * Render the entire `currentScale` viewport into the canvas with a translation that shifts the visible sub-rect to (0, 0).
     *
     * @param {number} pageNum One-based page number.
     * @returns {Promise<void>} Resolves once the overlay has been painted (or skipped).
     *
     * @memberof PdfViewer
     * @private
     */
    async #renderDetailOverlay(pageNum) {
        if (!this.#pdfDoc || !this.#detailCanvas || !this.canvasContainer || !this.wrapper) {
            return;
        }

        if (pageNum !== this.currentPage) {
            // Stale schedule from a previous page; the new page's render will reschedule.
            return;
        }

        const cacheEntry = this.#pageViewportCache.get(pageNum);

        if (!cacheEntry) {
            return;
        }

        // No sharpness benefit when the base canvas already covers the requested scale: hide the overlay and bail.
        // A small tolerance avoids re-rendering for sub-pixel float drift.
        if (this.currentScale <= cacheEntry.maxRenderScale + PdfViewer.SCALE_TRESHOLD) {
            this.#hideDetailOverlay();

            return;
        }

        const containerRect = this.canvasContainer.getBoundingClientRect();
        const wrapperRect = this.wrapper.getBoundingClientRect();

        if (wrapperRect.width <= 0 || wrapperRect.height <= 0) {
            return;
        }

        // Visible intersection in screen coordinates.
        const visibleLeft = Math.max(containerRect.left, wrapperRect.left);
        const visibleTop = Math.max(containerRect.top, wrapperRect.top);
        const visibleRight = Math.min(containerRect.right, wrapperRect.right);
        const visibleBottom = Math.min(containerRect.bottom, wrapperRect.bottom);

        if (visibleRight <= visibleLeft || visibleBottom <= visibleTop) {
            // Wrapper fully off-screen — nothing to paint.
            this.#hideDetailOverlay();

            return;
        }

        // Wrapper-local CSS coordinates. Panzoom does not apply CSS transforms, so wrapper bounding-rect dimensions equal its CSS box and
        // wrapper-local CSS pixels correspond 1:1 to a PDF page rendered at `currentScale`.
        const localLeft = visibleLeft - wrapperRect.left;
        const localTop = visibleTop - wrapperRect.top;
        const localWidth = visibleRight - visibleLeft;
        const localHeight = visibleBottom - visibleTop;

        const pixelRatio = window.devicePixelRatio || 1;

        // Cap the overlay's pixel area at `MAX_CANVAS_PIXELS` (the same iOS-aware cap used for the base canvas).
        // For typical viewport sizes the cap doesn't bite, but very large displays at high DPR could otherwise overshoot.
        const requestedPR = pixelRatio;
        const maxPRBySafeArea = Math.sqrt(PdfViewer.MAX_CANVAS_PIXELS / Math.max(1, localWidth * localHeight));
        const detailPR = Math.min(requestedPR, maxPRBySafeArea);

        const canvasW = Math.max(1, Math.floor(localWidth * detailPR));
        const canvasH = Math.max(1, Math.floor(localHeight * detailPR));

        // Cancel any earlier render before we start mutating shared state. PDF.js's cancel() makes the previous task throw `RenderingCancelledException`
        // from its promise, which the awaiter below catches.
        if (this.#detailRenderTask) {
            try {
                this.#detailRenderTask.cancel();
            }
            catch {
                // No-op.
            }

            this.#detailRenderTask = null;
        }

        let page;
        try {
            page = await this.#pdfDoc.getPage(pageNum);
        }
        catch (error) {
            window.isDebugMode && console.warn('Detail overlay getPage failed:', error);

            return;
        }

        // Re-check after the await: page change or teardown could have happened in flight.
        if (pageNum !== this.currentPage || !this.#detailCanvas || !this.#pdfDoc) {
            return;
        }

        const viewport = page.getViewport({ scale: this.currentScale });

        // Render PDF.js into an off-DOM buffer first, then atomically blit to the visible overlay canvas. Painting straight to `#detailCanvas` would
        // require resizing it (which clears its bitmap), giving the user a flash of blank/white during the render window. Buffering keeps the old
        // overlay visible until the new one is ready, so pan/zoom transitions stay seamless.
        if (!this.#detailBuffer) {
            this.#detailBuffer = document.createElement('canvas');
        }

        const buffer = this.#detailBuffer;

        buffer.width = canvasW;
        buffer.height = canvasH;

        const bufferCtx = buffer.getContext('2d', { alpha: false });

        bufferCtx.setTransform(1, 0, 0, 1, 0, 0);
        bufferCtx.fillStyle = '#ffffff';
        bufferCtx.fillRect(0, 0, canvasW, canvasH);

        // PDF.js applies this `transform` to the canvas before drawing the viewport. The matrix `[detailPR, 0, 0, detailPR, -localLeft × detailPR,
        // -localTop × detailPR]` scales viewport pixels (which are at currentScale, so they equal wrapper-local CSS pixels) up by detailPR and shifts
        // them so that `(localLeft, localTop)` lands at the canvas origin. Result: the visible sub-rectangle of the page fills the buffer.
        const transform = [detailPR, 0, 0, detailPR, -localLeft * detailPR, -localTop * detailPR];

        const renderTask = page.render({
            canvasContext: bufferCtx,
            transform,
            viewport
        });

        this.#detailRenderTask = renderTask;

        try {
            await renderTask.promise;
        }
        catch (error) {
            // PDF.js throws this when cancel() is called on an in-flight task; not an error we want to surface.
            if (error?.name === 'RenderingCancelledException') {
                return;
            }

            window.isDebugMode && console.warn('Detail overlay PDF render failed:', error);

            return;
        }

        // The render finished cleanly; check we're still the active task before swapping into the visible overlay.
        if (this.#detailRenderTask !== renderTask) {
            return;
        }

        this.#detailRenderTask = null;

        // Re-check the page hasn't changed during render before swapping.
        if (pageNum !== this.currentPage || !this.#detailCanvas) {
            return;
        }

        // Atomic swap: bring the visible overlay's bitmap and CSS box up to the freshly-rendered buffer in a single synchronous block. The browser
        // paints once, with no intermediate blank state.
        this.#detailCanvas.width = canvasW;
        this.#detailCanvas.height = canvasH;
        this.#detailCanvas.style.insetInlineStart = `${localLeft}px`;
        this.#detailCanvas.style.insetBlockStart = `${localTop}px`;
        this.#detailCanvas.style.inlineSize = `${localWidth}px`;
        this.#detailCanvas.style.blockSize = `${localHeight}px`;

        const visibleCtx = this.#detailCanvas.getContext('2d', { alpha: false });

        visibleCtx.setTransform(1, 0, 0, 1, 0, 0);
        visibleCtx.drawImage(buffer, 0, 0);

        this.#detailCanvas.hidden = false;
    }

    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register web component.
 * Defines the custom element 'pdf-viewer' and maps it to the PdfViewer class.
 */
customElements.define(PdfViewer.tagName, PdfViewer);
